import { inject, Injectable, NgZone, signal } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { SpeechRecognition } from '@capgo/capacitor-speech-recognition';

export type SpeechToTextErrorCode =
  | 'unavailable'
  | 'permission-denied'
  | 'start-failed';

export class SpeechToTextError extends Error {
  constructor(
    readonly code: SpeechToTextErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'SpeechToTextError';
  }
}

export type SpeechTranscriptCallback = (transcript: string) => void;

type WebSpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives?: number;
  onresult: ((event: WebSpeechRecognitionEvent) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
};

type WebSpeechAlternative = { transcript?: string };
type WebSpeechResult = {
  isFinal: boolean;
  length: number;
  item?: (index: number) => WebSpeechAlternative;
  [index: number]: WebSpeechAlternative;
};
type WebSpeechResultList = {
  length: number;
  item?: (index: number) => WebSpeechResult;
  [index: number]: WebSpeechResult;
};
type WebSpeechRecognitionEvent = {
  resultIndex?: number;
  results: WebSpeechResultList;
};

type WebSpeechRecognitionCtor = new () => WebSpeechRecognition;

const NATIVE_STOP_TIMEOUT_MS = 400;
const NATIVE_START_TIMEOUT_MS = 3000;
const NATIVE_RESTART_DELAY_MS = 280;
const NATIVE_BUSY_RESTART_DELAY_MS = 750;
const NATIVE_IGNORE_STOPPED_MS = 600;
const NATIVE_MAX_START_FAILURES = 4;

@Injectable({
  providedIn: 'root',
})
export class SpeechToTextService {
  private readonly platform = inject(Platform)

  readonly listening = signal(false);

  private starting = false;
  private keepWebListening = false;
  private keepNativeListening = false;
  private webLanguage = 'en-GB';
  private nativeLanguage = 'en-GB';
  private nativeListeners: PluginListenerHandle[] = [];
  private webRecognition: WebSpeechRecognition | null = null;
  private webFinalTranscript = '';
  private webCommittedTranscript = '';
  private nativeCommittedTranscript = '';
  private nativeCurrentTranscript = '';
  private nativeStartFailures = 0;
  private ignoreNativeStoppedUntil = 0;
  private nativeStartInFlight: Promise<void> | null = null;
  private onTranscript: SpeechTranscriptCallback | null = null;
  private onError: ((error: SpeechToTextError) => void) | null = null;
  private webStopTimer?: ReturnType<typeof setTimeout>;
  private webRestartTimer?: ReturnType<typeof setTimeout>;
  private nativeRestartTimer?: ReturnType<typeof setTimeout>;

  constructor(private ngZone: NgZone) {}

  async toggle(options: {
    language: string;
    onTranscript: SpeechTranscriptCallback;
    onError?: (error: SpeechToTextError) => void;
  }): Promise<void> {
    if (this.listening()) {
      await this.stop();
      return;
    }

    if (this.starting) {
      return;
    }

    this.starting = true;
    try {
      this.onTranscript = options.onTranscript;
      this.onError = options.onError ?? null;
      const language = toSpeechLocale(options.language);

      if (Capacitor.isNativePlatform()) {
        await this.startNative(language);
        return;
      }

      // Must run in the same tick as the click. Any await before start()
      // drops the browser user-gesture and Chrome/Safari capture no audio.
      this.startWeb(language);
    } finally {
      this.starting = false;
    }
  }

  async stop(): Promise<void> {
    this.keepWebListening = false;
    this.keepNativeListening = false;
    this.clearWebStopTimer();
    this.clearWebRestartTimer();
    this.clearNativeRestartTimer();

    try {
      if (Capacitor.isNativePlatform()) {
        await this.stopNativeRecognizer();
      } else if (this.webRecognition) {
        try {
          this.webRecognition.stop();
        } catch {
          // Already stopped.
        }
        // Final web results often arrive after stop(); wait for onend.
        this.webStopTimer = setTimeout(() => {
          if (!this.keepWebListening) {
            void this.cleanup();
          }
        }, 500);
        return;
      }
    } catch {
      // Already stopped or unimplemented on this platform.
    }

    await this.cleanup();
  }

  private async startNative(language: string): Promise<void> {
    try {
      const { available } = await SpeechRecognition.available();
      if (!available) {
        throw new SpeechToTextError('unavailable');
      }
    } catch (error) {
      if (error instanceof SpeechToTextError) {
        throw error;
      }
      throw new SpeechToTextError('unavailable');
    }

    const current = await SpeechRecognition.checkPermissions();
    const permission =
      current.speechRecognition === 'granted'
        ? current
        : await SpeechRecognition.requestPermissions();

    if (permission.speechRecognition !== 'granted') {
      throw new SpeechToTextError('permission-denied');
    }

    this.keepNativeListening = true;
    this.nativeLanguage = language;
    this.nativeCommittedTranscript = '';
    this.nativeCurrentTranscript = '';
    this.nativeStartFailures = 0;

    await this.ensureNativeListeners();
    this.ngZone.run(() => this.listening.set(true));

    try {
      await this.startNativeSession();
    } catch (error) {
      if (this.keepNativeListening && isRecoverableNativeError(error)) {
        this.scheduleNativeRestart(restartDelayFor(error));
        return;
      }

      this.keepNativeListening = false;
      await this.cleanup();
      throw new SpeechToTextError(
        'start-failed',
        error instanceof Error ? error.message : undefined,
      );
    }
  }

  private async ensureNativeListeners(): Promise<void> {
    if (this.nativeListeners.length > 0) {
      return;
    }

    this.nativeListeners = [
      await SpeechRecognition.addListener('partialResults', (data) => {
        const transcript = data.matches?.[0]?.trim();
        if (!transcript || !this.keepNativeListening) {
          return;
        }

        this.nativeCurrentTranscript = transcript;
        this.nativeStartFailures = 0;
        this.emitTranscript(this.readNativeTranscript());
      }),
      await SpeechRecognition.addListener('listeningState', (data) => {
        if (data.status === 'started') {
          this.ngZone.run(() => this.listening.set(true));
          return;
        }

        if (data.status !== 'stopped') {
          return;
        }

        // Android emits "stopped" at the end of every utterance, and a stale
        // stopped can arrive right after we start the next session.
        if (Date.now() < this.ignoreNativeStoppedUntil) {
          return;
        }

        if (!this.keepNativeListening) {
          return;
        }

        this.scheduleNativeRestart(NATIVE_RESTART_DELAY_MS);
      }),
    ];
  }

  private async startNativeSession(): Promise<void> {
    if (!this.keepNativeListening) {
      return;
    }

    if (this.nativeStartInFlight) {
      await this.nativeStartInFlight;
      return;
    }

    const start = this.runNativeStart();
    this.nativeStartInFlight = start;
    try {
      await start;
    } finally {
      if (this.nativeStartInFlight === start) {
        this.nativeStartInFlight = null;
      }
    }
  }

  private async runNativeStart(): Promise<void> {
    this.ignoreNativeStoppedUntil = Date.now() + NATIVE_IGNORE_STOPPED_MS;
    const { available: onDeviceRecognitionAvailable } =
      await SpeechRecognition.isOnDeviceRecognitionAvailable({
        language: this.nativeLanguage,
      });

    try {
      // popup:false is required for live partial results on Android.
      // See plugin docs: partialResults does not work when popup is true.
      await withTimeout(
        SpeechRecognition.start({
          language: this.nativeLanguage,
          maxResults: 1,
          partialResults: true,
          popup: false,
          useOnDeviceRecognition: (onDeviceRecognitionAvailable && this.platform.is('android')),
        }),
        NATIVE_START_TIMEOUT_MS,
      );
      this.nativeStartFailures = 0;
    } catch (error) {
      if (!this.keepNativeListening) {
        return;
      }

      if (
        isRecoverableNativeError(error) &&
        this.nativeStartFailures < NATIVE_MAX_START_FAILURES
      ) {
        this.nativeStartFailures += 1;
        this.scheduleNativeRestart(restartDelayFor(error));
        return;
      }

      throw error;
    }
  }

  private scheduleNativeRestart(delayMs: number): void {
    this.clearNativeRestartTimer();
    if (!this.keepNativeListening) {
      return;
    }

    this.nativeRestartTimer = setTimeout(() => {
      this.nativeRestartTimer = undefined;
      void this.restartNativeSession();
    }, delayMs);
  }

  private async restartNativeSession(): Promise<void> {
    if (!this.keepNativeListening) {
      return;
    }

    this.commitNativeTranscript();

    try {
      await this.startNativeSession();
    } catch (error) {
      this.keepNativeListening = false;
      const speechError = new SpeechToTextError(
        'start-failed',
        error instanceof Error ? error.message : undefined,
      );
      this.ngZone.run(() => this.onError?.(speechError));
      await this.cleanup();
    }
  }

  private commitNativeTranscript(): void {
    const spoken = this.readNativeTranscript();
    this.nativeCommittedTranscript = spoken;
    this.nativeCurrentTranscript = '';
    if (spoken) {
      this.emitTranscript(spoken);
    }
  }

  private readNativeTranscript(): string {
    return joinSpoken(
      this.nativeCommittedTranscript,
      this.nativeCurrentTranscript,
    );
  }

  private async stopNativeRecognizer(): Promise<void> {
    if (this.nativeStartInFlight) {
      await settleWithTimeout(this.nativeStartInFlight, NATIVE_STOP_TIMEOUT_MS);
    }

    // The community plugin never resolves stop() on Android. Do not await it
    // without a timeout or the mic button / dismiss path will hang.
    await settleWithTimeout(SpeechRecognition.stop(), NATIVE_STOP_TIMEOUT_MS);
  }

  private startWeb(language: string): void {
    const Recognition = getWebSpeechRecognition();
    if (!Recognition) {
      throw new SpeechToTextError('unavailable');
    }

    this.webLanguage = language;
    this.webFinalTranscript = '';
    this.webCommittedTranscript = '';
    this.keepWebListening = true;
    this.bindWebRecognition(new Recognition(), language);

    try {
      this.webRecognition?.start();
      this.listening.set(true);
    } catch (error) {
      this.keepWebListening = false;
      void this.cleanup();
      if (isNotAllowedError(error)) {
        throw new SpeechToTextError('permission-denied');
      }
      throw new SpeechToTextError(
        'unavailable',
        error instanceof Error ? error.message : undefined,
      );
    }
  }

  private bindWebRecognition(
    recognition: WebSpeechRecognition,
    language: string,
  ): void {
    recognition.lang = language;
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const transcript = this.readWebTranscript(event);
      if (transcript) {
        this.emitTranscript(transcript);
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'aborted' || event.error === 'no-speech') {
        return;
      }

      if (event.error === 'language-not-supported' && language !== 'en-US') {
        this.webLanguage = 'en-US';
        return;
      }

      this.keepWebListening = false;
      const error = new SpeechToTextError(
        event.error === 'not-allowed' ? 'permission-denied' : 'start-failed',
      );
      this.ngZone.run(() => this.onError?.(error));
    };

    recognition.onend = () => {
      this.webRecognition = null;
      if (!this.keepWebListening) {
        void this.cleanup();
        return;
      }

      this.webCommittedTranscript =
        `${this.webCommittedTranscript} ${this.webFinalTranscript}`.trim();
      this.webFinalTranscript = '';
      this.clearWebRestartTimer();
      this.webRestartTimer = setTimeout(
        () => this.restartWebRecognition(),
        180,
      );
    };

    this.webRecognition = recognition;
  }

  private restartWebRecognition(): void {
    const Recognition = getWebSpeechRecognition();
    if (!Recognition || !this.keepWebListening) {
      return;
    }

    this.bindWebRecognition(new Recognition(), this.webLanguage);
    try {
      this.webRecognition?.start();
      this.ngZone.run(() => this.listening.set(true));
    } catch {
      this.keepWebListening = false;
      void this.cleanup();
    }
  }

  private readWebTranscript(event: WebSpeechRecognitionEvent): string {
    const results = event.results;
    let sessionFinal = '';
    let interim = '';

    for (let i = 0; i < results.length; i++) {
      const result = results.item ? results.item(i) : results[i];
      if (!result) {
        continue;
      }

      const alternative = result.item ? result.item(0) : result[0];
      const piece = alternative?.transcript?.trim() ?? '';
      if (!piece) {
        continue;
      }

      if (result.isFinal) {
        sessionFinal = `${sessionFinal} ${piece}`.trim();
      } else {
        interim = `${interim} ${piece}`.trim();
      }
    }

    this.webFinalTranscript = sessionFinal;
    return `${this.webCommittedTranscript} ${sessionFinal} ${interim}`.trim();
  }

  private emitTranscript(transcript: string): void {
    this.ngZone.run(() => {
      this.onTranscript?.(transcript);
    });
  }

  private async cleanup(): Promise<void> {
    this.clearWebStopTimer();
    this.clearWebRestartTimer();
    this.clearNativeRestartTimer();
    this.keepWebListening = false;
    this.keepNativeListening = false;
    this.webRecognition = null;
    this.webFinalTranscript = '';
    this.webCommittedTranscript = '';
    this.nativeCommittedTranscript = '';
    this.nativeCurrentTranscript = '';
    this.nativeStartFailures = 0;
    this.nativeStartInFlight = null;
    this.onTranscript = null;
    this.onError = null;
    await this.clearNativeListeners();
    this.ngZone.run(() => this.listening.set(false));
  }

  private clearWebStopTimer(): void {
    if (this.webStopTimer) {
      clearTimeout(this.webStopTimer);
      this.webStopTimer = undefined;
    }
  }

  private clearWebRestartTimer(): void {
    if (this.webRestartTimer) {
      clearTimeout(this.webRestartTimer);
      this.webRestartTimer = undefined;
    }
  }

  private clearNativeRestartTimer(): void {
    if (this.nativeRestartTimer) {
      clearTimeout(this.nativeRestartTimer);
      this.nativeRestartTimer = undefined;
    }
  }

  private async clearNativeListeners(): Promise<void> {
    const listeners = this.nativeListeners;
    this.nativeListeners = [];
    await Promise.all(
      listeners.map(async (listener) => {
        try {
          await listener.remove();
        } catch {
          // Listener already removed.
        }
      }),
    );
  }
}

function toSpeechLocale(language: string): string {
  const normalized = language.trim().toLowerCase();
  if (normalized === 'de' || normalized.startsWith('de-')) {
    return 'de-DE';
  }
  if (normalized === 'en-gb' || normalized.startsWith('en-gb')) {
    return 'en-GB';
  }
  if (normalized.startsWith('en')) {
    return 'en-US';
  }
  return language || 'en-GB';
}

function getWebSpeechRecognition(): WebSpeechRecognitionCtor | null {
  const host = window as Window & {
    SpeechRecognition?: WebSpeechRecognitionCtor;
    webkitSpeechRecognition?: WebSpeechRecognitionCtor;
  };
  return host.SpeechRecognition ?? host.webkitSpeechRecognition ?? null;
}

function isNotAllowedError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'NotAllowedError' || error.name === 'SecurityError')
  );
}

function joinSpoken(...parts: string[]): string {
  return parts
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(' ');
}

function nativeErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message.toLowerCase();
  }
  return String(error ?? '').toLowerCase();
}

function isRecoverableNativeError(error: unknown): boolean {
  const message = nativeErrorMessage(error);
  return (
    message.includes('no match') ||
    message.includes('no speech') ||
    message.includes('client side') ||
    message.includes('busy') ||
    message.includes('network timeout') ||
    message.includes('timeout') ||
    message.includes("didn't understand")
  );
}

function restartDelayFor(error: unknown): number {
  return nativeErrorMessage(error).includes('busy')
    ? NATIVE_BUSY_RESTART_DELAY_MS
    : NATIVE_RESTART_DELAY_MS;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

async function settleWithTimeout(
  promise: Promise<unknown>,
  ms: number,
): Promise<void> {
  try {
    await withTimeout(promise, ms);
  } catch {
    // Timed out or already stopped.
  }
}
